/**
 * studioApi.ts
 *
 * Re-exported from @crosure/tenant-runtime — kept so existing imports of
 * `@/lib/studioApi` keep working unchanged. New code should import the
 * client directly from @crosure/tenant-runtime instead.
 *
 * Environment variables (server-side only), unchanged from before:
 *   STUDIO_API_URL   — base URL of the Studio backend, e.g. https://studio.crosure.com
 *   STUDIO_API_KEY   — raw API key issued from Studio (TenantAPIKey).
 */

import {
	createStudioApiClient,
	StudioApiError,
	StudioLoginError,
} from '@crosure/tenant-runtime'
import type {
	StudioApiClient,
	StudioSectionMeta,
	StudioFormSchemaResponse,
	StudioFieldSchema,
	StudioUiEntry,
	StudioSubmitResponse,
	LiveVirtualAgent,
	StudioSiteNavigationResponse,
	StudioThemeResponse,
	StudioPageSection,
	StudioPageSchema,
	StudioQuotePreviewResponse,
	StudioBulkJob,
	StudioPaymentIntent,
	StudioPaymentMethod,
	StudioPaymentMethodsResponse,
	StudioBillingPlan,
	StudioFormContract,
	StudioWizardFormSchemaResponse,
} from '@crosure/tenant-runtime'

export { StudioApiError, StudioLoginError }
export type {
	StudioSectionMeta,
	StudioFormSchemaResponse,
	StudioFieldSchema,
	StudioUiEntry,
	StudioSubmitResponse,
	LiveVirtualAgent,
	StudioSiteNavigationResponse,
	StudioThemeResponse,
	StudioPageSection,
	StudioPageSchema,
	StudioQuotePreviewResponse,
	StudioBulkJob,
	StudioPaymentIntent,
	StudioPaymentMethod,
	StudioPaymentMethodsResponse,
	StudioBillingPlan,
	StudioFormContract,
	StudioWizardFormSchemaResponse,
}

function requireEnv(name: 'STUDIO_API_URL' | 'STUDIO_API_KEY'): string {
	const v = process.env[name]
	if (!v) throw new Error(`${name} is not set`)
	return v
}

export function studioClient(): StudioApiClient {
	return createStudioApiClient({
		apiBase: requireEnv('STUDIO_API_URL'),
		apiKey: requireEnv('STUDIO_API_KEY'),
	})
}

export function fetchStudioFormSchema(
	formCode: string,
	locale = 'en',
	contract?: StudioFormContract
): Promise<StudioFormSchemaResponse | StudioWizardFormSchemaResponse> {
	return studioClient().fetchStudioFormSchema(formCode, locale, contract)
}

/**
 * Fetch the RJSF-compatible form schema for a product line, resolved by
 * product_code instead of a hardcoded form_code. Lets a tenant route
 * (e.g. /forms/home) ask "whatever form is currently approved for this
 * product" instead of baking a form_code into the tenant package —
 * publishing a new approved FormDefinition for the same product_code
 * updates what this returns with no tenant redeploy.
 * Requires scopes: forms:read
 */
export function fetchStudioFormSchemaByProduct(
	productCode: string,
	locale = 'en'
): Promise<StudioFormSchemaResponse> {
	return studioClient().fetchStudioFormSchemaByProduct(productCode, locale)
}

// Page Builder and Sponsor Enrollment endpoints below aren't part of the
// @crosure/tenant-runtime client yet, so they still call the Studio Client
// API directly and need these two helpers.
export function studioBase(): string {
	const url = process.env.STUDIO_API_URL
	if (!url) throw new Error('STUDIO_API_URL is not set')
	return url.replace(/\/$/, '')
}

export function studioAuthHeaders(): Record<string, string> {
	const key = process.env.STUDIO_API_KEY
	if (!key) throw new Error('STUDIO_API_KEY is not set')
	return {
		Accept: 'application/json',
		Authorization: `Bearer ${key}`,
	}
}

export function fetchLiveVirtualAgent(
	section: string
): Promise<LiveVirtualAgent | null> {
	return studioClient().fetchLiveVirtualAgent(section)
}

export async function fetchSiteNavigation(
	locale = ''
): Promise<StudioSiteNavigationResponse> {
	if (!locale) return studioClient().fetchSiteNavigation()
	const response = await fetch(
		`${studioBase()}/api/client/v1/site-navigation/?locale=${encodeURIComponent(locale)}`,
		{ headers: studioAuthHeaders(), signal: AbortSignal.timeout(10000) }
	)
	const body = await response.json().catch(() => ({}))
	if (!response.ok) throw new StudioApiError(response.status, body)
	return body as StudioSiteNavigationResponse
}

/** GET /api/client/v1/page-shell/ — the tenant root's props for a surface
 *  that is not a Studio page (the quote forms). `enabled` is false unless the
 *  tenant opted in (page_builder.root_on_forms) and has reported a root.
 *
 *  A direct fetch rather than a studioClient() method so this does not wait
 *  on a @crosure/tenant-runtime release; fold it in on the next one. */
export interface StudioPageShellResponse {
	enabled: boolean
	root: Record<string, unknown> | null
}

export async function fetchPageShell(
	locale = ''
): Promise<StudioPageShellResponse> {
	const params = locale ? `?locale=${encodeURIComponent(locale)}` : ''
	const response = await fetch(
		`${studioBase()}/api/client/v1/page-shell/${params}`,
		// Shorter than the page/schema calls: this only decorates the form, and
		// a slow answer must not hold up the quote it sits on.
		{ headers: studioAuthHeaders(), signal: AbortSignal.timeout(5000) }
	)
	const body = await response.json().catch(() => ({}))
	if (!response.ok) throw new StudioApiError(response.status, body)
	return body as StudioPageShellResponse
}

export interface StudioLocalizationResponse {
	locale: string
	default_locale: string
	supported_locales: string[]
	direction: 'ltr' | 'rtl'
	messages: Record<string, unknown>
	resources: Record<string, Record<string, unknown>>
	language_switcher_visible?: boolean
	language_switcher_position?: 'left' | 'center' | 'right'
	/** Regional formatting, from the tenant's Studio settings. All optional —
	 *  blank/absent means "not configured", so a caller falls back to plain
	 *  locale-driven Intl behaviour rather than guessing. */
	date_format?: string
	time_format?: string
	number_format?: string
	week_starts_on?: string
	default_currency?: string
	timezone?: string
}

export async function fetchLocalization(
	locale = ''
): Promise<StudioLocalizationResponse> {
	const params = locale ? `?locale=${encodeURIComponent(locale)}` : ''
	const response = await fetch(
		`${studioBase()}/api/client/v1/localization/${params}`,
		{ headers: studioAuthHeaders(), signal: AbortSignal.timeout(10000) }
	)
	const body = await response.json().catch(() => ({}))
	if (!response.ok) throw new StudioApiError(response.status, body)
	return body as StudioLocalizationResponse
}

export interface StudioProductCard {
	code: string
	found: boolean
	name?: string
	summary?: string
	premiumFrom?: string
	productClass?: string
}

/**
 * Resolve product CODES into page-builder card data.
 *
 * Pages store codes, never copies of product data (BYOD Phase 8), so a
 * repriced or renamed product never requires re-editing the pages that
 * mention it. Called server-side during render: the section dispatcher is
 * synchronous, so a section needing remote data has to receive it.
 *
 * Never throws — a marketing page must not 500 because a product lookup
 * hiccuped. An empty result renders the card's own "no products" state.
 */
export async function fetchProductCards(
	codes: string[],
	locale = ''
): Promise<StudioProductCard[]> {
	const unique = Array.from(new Set(codes.filter(Boolean)))
	if (unique.length === 0) return []
	try {
		const params = new URLSearchParams({ codes: unique.join(',') })
		if (locale) params.set('locale', locale)
		const response = await fetch(
			`${studioBase()}/api/client/v1/products/cards/?${params.toString()}`,
			{ headers: studioAuthHeaders(), signal: AbortSignal.timeout(10000) }
		)
		if (!response.ok) return []
		const body = (await response.json()) as { products?: StudioProductCard[] }
		return body.products ?? []
	} catch {
		return []
	}
}

export function fetchTheme(): Promise<StudioThemeResponse> {
	return studioClient().fetchTheme()
}

export async function submitToStudio(
	formCode: string,
	data: Record<string, unknown>,
	sourceChannel = 'web',
	externalReference?: string,
	otpVerifications?: Record<string, string>,
	locale?: string,
	/** Signed statements of what each integration lookup wrote. Studio checks
	 *  the API-owned answers against these before rating, so a journey that
	 *  ran a lookup must forward them or those answers are refused. */
	integrationProvenance?: string[]
): Promise<StudioSubmitResponse> {
	if (
		locale ||
		integrationProvenance?.length ||
		(otpVerifications && Object.keys(otpVerifications).length > 0)
	) {
		const response = await fetch(
			`${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/submit/`,
			{
				method: 'POST',
				headers: {
					...studioAuthHeaders(),
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					data,
					source_channel: sourceChannel,
					external_reference: externalReference,
					otp_verifications: otpVerifications,
					integration_provenance: integrationProvenance,
					locale,
				}),
				signal: AbortSignal.timeout(20000),
			}
		)
		const body = await response.json().catch(() => ({}))
		if (!response.ok) throw new StudioApiError(response.status, body)
		return body as StudioSubmitResponse
	}
	return studioClient().submitToStudio(
		formCode,
		data,
		sourceChannel,
		externalReference
	)
}

export async function requestStudioFormOtp(
	formCode: string,
	payload: {
		section_code: string
		mobile_number: string
		challenge_id?: string
		resend?: boolean
		locale?: string
	}
): Promise<Record<string, unknown>> {
	return postStudioFormOtp(formCode, 'request', payload)
}

export async function verifyStudioFormOtp(
	formCode: string,
	payload: { section_code: string; challenge_id: string; code: string }
): Promise<Record<string, unknown>> {
	return postStudioFormOtp(formCode, 'verify', payload)
}

async function postStudioFormOtp(
	formCode: string,
	action: 'request' | 'verify',
	payload: Record<string, unknown>
): Promise<Record<string, unknown>> {
	const response = await fetch(
		`${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/otp/${action}/`,
		{
			method: 'POST',
			headers: {
				...studioAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
			signal: AbortSignal.timeout(10000),
		}
	)
	const body = await response.json().catch(() => ({}))
	if (!response.ok) throw new StudioApiError(response.status, body)
	return body as Record<string, unknown>
}

export function captureLeadInStudio(
	formCode: string,
	data: Record<string, unknown>,
	meta?: {
		reason?: string
		reason_code?: string
		triggered_rule?: string
		source?: string
	}
): Promise<{ lead_id: string; status: string } | null> {
	return studioClient().captureLeadInStudio(formCode, data, meta)
}

export function previewQuoteFromStudio(
	formCode: string,
	data: Record<string, unknown>,
	locale?: string
): Promise<StudioQuotePreviewResponse> {
	if (locale) {
		return fetch(
			`${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/quote-preview/`,
			{
				method: 'POST',
				headers: {
					...studioAuthHeaders(),
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ data, locale }),
				signal: AbortSignal.timeout(20000),
			}
		).then(async response => {
			const body = await response.json().catch(() => ({}))
			if (!response.ok) throw new StudioApiError(response.status, body)
			return body as StudioQuotePreviewResponse
		})
	}
	return studioClient().previewQuoteFromStudio(formCode, data)
}

export type StudioIntegrationActionResponse = {
	mapping_code: string
	target_code: string
	adapter: string
	fixture_code?: string | null
	status_code: number
	request_payload: Record<string, unknown>
	response_payload: unknown
	field_values: Record<string, unknown>
	/** Present only for a row-scoped mapping: the repeater field and the row
	 *  `field_values` belongs to. A caller that merged those values at the top
	 *  level instead would write a driver's licence status over a same-named
	 *  form answer. */
	row_scope?: string
	row_index?: number
}

/** Execute one Studio-authored on-demand mapping. This stays server-side so
 * live target credentials and the tenant API key never reach the browser. */
export async function executeStudioIntegrationAction(
	formCode: string,
	mappingCode: string,
	data: Record<string, unknown>,
	locale?: string,
	rowIndex?: number
): Promise<StudioIntegrationActionResponse> {
	const response = await fetch(
		`${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/actions/${encodeURIComponent(mappingCode)}/execute/`,
		{
			method: 'POST',
			headers: {
				...studioAuthHeaders(),
				'Content-Type': 'application/json',
			},
			// `row_index` is read only by a mapping that declares `row_scope` —
			// a per-row lookup, such as one driver's licence. Studio ignores it
			// on any other mapping, so the caller need not know how the mapping
			// is configured before sending the row it fired from.
			body: JSON.stringify({
				data,
				locale,
				...(typeof rowIndex === 'number' ? { row_index: rowIndex } : {}),
			}),
			signal: AbortSignal.timeout(20000),
		}
	)
	const body = await response.json().catch(() => ({}))
	if (!response.ok) throw new StudioApiError(response.status, body)
	return body as StudioIntegrationActionResponse
}

/**
 * Studio's built-in postcode → address lookup (OS Places, degrading to
 * postcodes.io). Results carry the provider's own component names; see
 * docs/STUDIO_INTEGRATION.md for the per-source key list.
 *
 * The view itself is AllowAny, but this stays server-side with the rest: the
 * storefront's origin is not in Studio's CORS allowlist, and adding every
 * tenant's domain there to save one hop is not a trade worth making.
 */
export async function fetchStudioPostcodeLookup(
	postcode: string,
	country = 'gb'
): Promise<Array<Record<string, unknown>>> {
	// Unlike every other client endpoint this one is AllowAny, so a deployment
	// with no key still resolves addresses — `studioAuthHeaders` would throw and
	// take that away.
	const key = process.env.STUDIO_API_KEY
	const response = await fetch(
		`${studioBase()}/api/client/v1/lookups/postcode/?postcode=${encodeURIComponent(postcode)}&country=${encodeURIComponent(country)}`,
		{
			headers: {
				Accept: 'application/json',
				...(key ? { Authorization: `Bearer ${key}` } : {}),
			},
			signal: AbortSignal.timeout(10000),
		}
	)
	const body = await response.json().catch(() => ({}))
	if (!response.ok) throw new StudioApiError(response.status, body)
	// Upstream failures are absorbed into a 200 with `results: []` (sometimes
	// plus an `error` string), so an empty list means "no match", not a fault.
	return (body?.results ?? []) as Array<Record<string, unknown>>
}

export function submitStudioBulk(
	formCode: string,
	rows: Array<Record<string, unknown>>,
	partnerReference?: string
): Promise<StudioBulkJob> {
	return studioClient().submitStudioBulk(formCode, rows, partnerReference)
}

export function fetchStudioBulkJob(jobId: string): Promise<StudioBulkJob> {
	return studioClient().fetchStudioBulkJob(jobId)
}

export async function fetchStudioPaymentMethods(
	formCode: string,
	reference?: string,
	paymentOption?: string
): Promise<StudioPaymentMethod[]> {
	const response = await fetchStudioPaymentMethodsResponse(
		formCode,
		reference,
		paymentOption
	)
	return response.methods
}

/**
 * Channels *and* the billing block, exactly as Studio returned them.
 *
 * Anything that renders an amount or starts a payment must use this: the
 * currency lives in `billing`, and a checkout that has to guess it will guess
 * from `NEXT_PUBLIC_CURRENCY`.
 */
/** The insurer a quote is bound to, as the checkout names it. */
export type StudioPaymentUnderwriter = { code: string; name: string }

export async function fetchStudioPaymentMethodsResponse(
	formCode: string,
	reference?: string,
	paymentOption?: string,
	locale?: string
): Promise<
	StudioPaymentMethodsResponse & {
		underwriter: StudioPaymentUnderwriter | null
	}
> {
	const params = new URLSearchParams()
	if (reference) params.set('reference', reference)
	if (paymentOption) params.set('payment_option', paymentOption)
	// Names the underwriter in the checkout's language.
	if (locale) params.set('locale', locale)
	const query = params.toString()
	const url = `${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/payment-methods/${query ? `?${query}` : ''}`
	const res = await fetch(url, {
		headers: studioAuthHeaders(),
		signal: AbortSignal.timeout(12000),
	})
	const body = await res.json().catch(() => ({}))
	if (!res.ok) throw new StudioApiError(res.status, body)
	return {
		methods: Array.isArray(body?.methods) ? body.methods : [],
		billing: (body?.billing ?? null) as StudioBillingPlan | null,
		underwriter:
			body?.underwriter && typeof body.underwriter.name === 'string'
				? {
						code: String(body.underwriter.code ?? ''),
						name: body.underwriter.name,
					}
				: null,
	}
}

/** A persisted quote's detail (`GET /api/client/v1/quotes/{reference}/`). */
export async function fetchStudioQuoteDetail(
	reference: string
): Promise<Record<string, unknown>> {
	const res = await fetch(
		`${studioBase()}/api/client/v1/quotes/${encodeURIComponent(reference)}/`,
		{ headers: studioAuthHeaders(), signal: AbortSignal.timeout(12000) }
	)
	const body = await res.json().catch(() => ({}))
	if (!res.ok) throw new StudioApiError(res.status, body)
	return body as Record<string, unknown>
}

export async function createStudioPaymentIntent(
	formCode: string,
	amountMinor: number,
	currency: string,
	reference: string,
	metadata?: Record<string, unknown>,
	provider?: string,
	paymentChannel?: string,
	paymentOption?: string,
	locale?: string
): Promise<StudioPaymentIntent> {
	const url = `${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/payment-intent/`
	const res = await fetch(url, {
		method: 'POST',
		headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
		body: JSON.stringify({
			amount: amountMinor,
			currency,
			reference,
			...(metadata ? { metadata } : {}),
			...(provider ? { provider } : {}),
			...(paymentChannel ? { payment_channel: paymentChannel } : {}),
			...(paymentOption ? { payment_option: paymentOption } : {}),
			...(locale ? { locale } : {}),
		}),
		signal: AbortSignal.timeout(20000),
	})
	const body = await res.json().catch(() => ({}))
	if (!res.ok) throw new StudioApiError(res.status, body)
	return body as StudioPaymentIntent
}

export function loginStudioAccount(
	formCode: string,
	email: string,
	password: string
): Promise<{ ok: boolean; token: string; email: string }> {
	return studioClient().loginStudioAccount(formCode, email, password)
}

/** Studio's answer to set-password, status and body untouched. The page
 * tells the outcomes apart by status and `code` (a bulk purchase answers
 * 409 payment_pending / account_exists / email_missing, 429, 400 password
 * rules …), so nothing here may turn one into a generic failure. */
export interface StudioSetPasswordResult {
	status: number
	body: Record<string, unknown>
	retryAfter: string | null
}

export async function setStudioAccountPassword(
	formCode: string,
	reference: string,
	password: string,
	email?: string
): Promise<StudioSetPasswordResult> {
	const res = await fetch(
		`${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/account/set-password/`,
		{
			method: 'POST',
			headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
			body: JSON.stringify(
				email ? { reference, password, email } : { reference, password }
			),
		}
	)
	const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
	return {
		status: res.status,
		body,
		retryAfter: res.headers.get('retry-after'),
	}
}

/** Studio's answer to account/register, status and body untouched: the
 * login step tells 201, 400 (field errors / otp_invalid), 404 and 409
 * account_exists apart by them. */
export async function registerStudioAccount(
	formCode: string,
	body: {
		email: string
		password: string
		mobile_number: string
		verification_token: string
		terms_accepted: boolean
	}
): Promise<{ status: number; body: Record<string, unknown> }> {
	const res = await fetch(
		`${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/account/register/`,
		{
			method: 'POST',
			headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
			body: JSON.stringify(body),
		}
	)
	const out = (await res.json().catch(() => ({}))) as Record<string, unknown>
	return { status: res.status, body: out }
}

export async function requestStudioPasswordReset(
	formCode: string,
	email: string,
	locale = ''
): Promise<{ detail: string }> {
	const res = await fetch(
		`${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/account/password-reset/request/`,
		{
			method: 'POST',
			headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
			body: JSON.stringify({ email, ...(locale ? { locale } : {}) }),
		}
	)
	if (!res.ok) {
		const detail = await res.text()
		throw new Error(
			`Studio password-reset request failed (${res.status}): ${detail.slice(0, 300)}`
		)
	}
	return (await res.json()) as { detail: string }
}

export function confirmStudioPasswordReset(
	formCode: string,
	token: string,
	password: string
): Promise<{ ok: boolean; email: string }> {
	return studioClient().confirmStudioPasswordReset(formCode, token, password)
}

// ---------------------------------------------------------------------------
// Page Builder — Studio-built landing/static pages
// ---------------------------------------------------------------------------

export interface StudioArticleCard {
	article_code: string
	slug: string
	title: string
	hero_image_url: string
	author_display_name: string
	published_at: string | null
	meta_description: string
}

/** Full article — `body` is HTML already sanitized by Studio on save
 *  (apps/studio_core/html_sanitizer.py). The lite app renders it as-is and
 *  must never re-trust or re-clean client-side; Studio is the boundary. */
export interface StudioArticle extends StudioArticleCard {
	body: string
}

/**
 * Published articles for this tenant, newest first. Backs the `blog_listing`
 * page section's two modes: latest N, or an explicit slug list.
 * Returns [] on any failure — a broken blog listing must not 500 a page
 * whose other sections are fine.
 */
export async function fetchArticles(
	opts: { limit?: number; slugs?: string[] } = {}
): Promise<StudioArticleCard[]> {
	const qs = new URLSearchParams()
	if (opts.limit) qs.set('limit', String(opts.limit))
	if (opts.slugs?.length) qs.set('slugs', opts.slugs.join(','))
	const suffix = qs.toString() ? `?${qs.toString()}` : ''
	try {
		const res = await fetch(
			`${studioBase()}/api/client/v1/articles/${suffix}`,
			{ headers: studioAuthHeaders(), signal: AbortSignal.timeout(12000) }
		)
		if (!res.ok) return []
		const body = (await res.json()) as { articles?: StudioArticleCard[] }
		return body.articles ?? []
	} catch {
		return []
	}
}

/**
 * One published article by slug. Returns null when missing/unpublished/
 * another tenant's (Studio answers 404 for all three, deliberately
 * indistinguishable) — the caller renders notFound().
 */
export async function fetchArticle(
	slug: string
): Promise<StudioArticle | null> {
	const res = await fetch(
		`${studioBase()}/api/client/v1/articles/${encodeURIComponent(slug)}/`,
		{ headers: studioAuthHeaders(), signal: AbortSignal.timeout(12000) }
	)
	if (res.status === 404) return null
	if (!res.ok) {
		const body = await res.json().catch(() => ({}))
		throw new StudioApiError(res.status, body)
	}
	return res.json() as Promise<StudioArticle>
}

/**
 * Fetch a published Studio page's section schema (tenant-scoped) from the
 * Client API. Requires scope: forms:read. Runs server-side only.
 * Returns null when the page is not found / not published for this tenant
 * (404) — the caller renders a Next notFound().
 */
export async function fetchPageSchema(
	slug: string,
	locale = ''
): Promise<StudioPageSchema | null> {
	return studioClient().fetchPageSchema(slug, locale)
}

/** Fetch an unpublished page for Studio's short-lived, tenant-bound preview. */
export async function fetchPagePreviewSchema(
	slug: string,
	token: string,
	locale = ''
): Promise<StudioPageSchema | null> {
	return studioClient().fetchPagePreviewSchema(slug, token, locale)
}

/**
 * Fetch the tenant's flagged home page (Studio Pages list → "Set as Home"),
 * resolved server-side by the tenant's API key rather than a hardcoded slug.
 * Returns null when no page is flagged / published — the caller (pages/
 * index.tsx) falls back to /home rather than rendering nothing.
 */
export async function fetchHomePageSchema(
	locale = ''
): Promise<StudioPageSchema | null> {
	const params = locale ? `?locale=${encodeURIComponent(locale)}` : ''
	const url = `${studioBase()}/api/client/v1/pages/home/schema/${params}`
	const res = await fetch(url, {
		headers: studioAuthHeaders(),
		signal: AbortSignal.timeout(12000),
	})
	if (res.status === 404) return null
	if (!res.ok) {
		const body = await res.json().catch(() => ({}))
		throw new StudioApiError(res.status, body)
	}
	return res.json() as Promise<StudioPageSchema>
}

// ---------------------------------------------------------------------------
// Provider (underwriter) picker — pre-wizard gate page
// ---------------------------------------------------------------------------

export interface StudioProviderOptions {
	opted_in: boolean
	field_code: string | null
	options: Array<{ value: string; label: string }>
}

/**
 * Whether a form has opted into a client-facing provider (underwriter)
 * picker, and if so, the currently active options. Called server-side from
 * /forms/[formCode]'s entry route, BEFORE the wizard schema is ever
 * fetched — the picker is a pre-wizard gate page, not a step inside the
 * wizard, so it needs its own lightweight lookup rather than riding along
 * on fetchStudioFormSchema.
 *
 * Fails open: any error (form not found, Studio unreachable, env vars
 * unset) resolves to "no picker" rather than throwing, so a misconfigured
 * or unreachable Studio never blocks the journey — the entry route just
 * falls back to today's instant-redirect behavior.
 */
export async function fetchProviderOptions(
	formCode: string,
	locale = ''
): Promise<StudioProviderOptions> {
	const empty: StudioProviderOptions = {
		opted_in: false,
		field_code: null,
		options: [],
	}
	try {
		const params = locale ? `?locale=${encodeURIComponent(locale)}` : ''
		const url = `${studioBase()}/api/v1/form-schemas/${encodeURIComponent(formCode)}/provider-options/${params}`
		const res = await fetch(url, {
			headers: studioAuthHeaders(),
			signal: AbortSignal.timeout(8000),
		})
		if (!res.ok) return empty
		return (await res.json()) as StudioProviderOptions
	} catch {
		return empty
	}
}

/**
 * Sponsor Enrollment Engine (docs/specs/sponsor-enrollment-engine-plan.md).
 * Verify a sponsor's email + password and return the signed sponsor bearer
 * token. No formCode needed — sponsor login is API-key authenticated
 * directly (the tenant comes from the key), unlike customer login which is
 * nested under a form's tenant. Server-side only (uses STUDIO_API_KEY).
 */
export async function loginSponsorAccount(
	email: string,
	password: string
): Promise<{ ok: boolean; token: string; organization_name: string }> {
	const res = await fetch(`${studioBase()}/api/client/v1/sponsor/login/`, {
		method: 'POST',
		headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
		body: JSON.stringify({ email, password }),
	})
	if (res.status === 401) {
		throw new StudioLoginError(401, 'Invalid email or password.')
	}
	if (!res.ok) {
		const detail = await res.text()
		throw new Error(
			`Studio sponsor login failed (${res.status}): ${detail.slice(0, 300)}`
		)
	}
	return (await res.json()) as {
		ok: boolean
		token: string
		organization_name: string
	}
}

/**
 * Sales dashboard tenant-admin persona
 * (docs/specs/sales-dashboard-tenant-admin-plan.md). Same shape as
 * loginSponsorAccount — API-key authenticated, tenant resolved from the
 * key, no formCode. Server-side only (uses STUDIO_API_KEY).
 */
export async function loginTenantAdminAccount(
	email: string,
	password: string
): Promise<TenantAdminLoginResult> {
	const res = await fetch(`${studioBase()}/api/client/v1/tenant-admin/login/`, {
		method: 'POST',
		headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
		body: JSON.stringify({ email, password }),
	})
	if (res.status === 401) {
		throw new StudioLoginError(401, 'Invalid email or password.')
	}
	if (!res.ok) {
		const detail = await res.text()
		throw new Error(
			`Studio tenant-admin login failed (${res.status}): ${detail.slice(0, 300)}`
		)
	}
	return (await res.json()) as TenantAdminLoginResult
}

export interface TenantAdminLoginResult {
	ok: boolean
	token: string
	email: string
	full_name: string
	/** Absent on a Studio older than bulk policies (= staff). */
	access_scope?: 'tenant' | 'granted_policies'
}

export async function requestTenantAdminPasswordReset(
	email: string,
	locale = ''
): Promise<{ detail: string }> {
	const res = await fetch(
		`${studioBase()}/api/client/v1/tenant-admin/password-reset/request/`,
		{
			method: 'POST',
			headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
			body: JSON.stringify({ email, ...(locale ? { locale } : {}) }),
		}
	)
	if (!res.ok) {
		const detail = await res.text()
		throw new Error(
			`Studio tenant-admin password-reset request failed (${res.status}): ${detail.slice(0, 300)}`
		)
	}
	return (await res.json()) as { detail: string }
}

export async function confirmTenantAdminPasswordReset(
	token: string,
	password: string
): Promise<{ ok: boolean; email: string }> {
	const res = await fetch(
		`${studioBase()}/api/client/v1/tenant-admin/password-reset/confirm/`,
		{
			method: 'POST',
			headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
			body: JSON.stringify({ token, password }),
		}
	)
	const body = await res.json().catch(() => ({}))
	if (!res.ok) {
		throw new Error(
			(body as { detail?: string }).detail ??
				`Studio tenant-admin password-reset confirm failed (${res.status})`
		)
	}
	return body as { ok: boolean; email: string }
}

/**
 * Any one approved form_code for the calling tenant — for account flows
 * (login, forgot/reset password, set-password) that only need *some* valid
 * form_code to satisfy Studio's tenant-existence check, not a specific
 * product's form. See lib/studioForms.ts::resolveAccountFormCode() for the
 * full resolution order this backs as the last-resort step.
 */
export async function fetchDefaultFormCode(): Promise<string | null> {
	try {
		const res = await fetch(
			`${studioBase()}/api/client/v1/account/default-form/`,
			{ headers: studioAuthHeaders(), signal: AbortSignal.timeout(8000) }
		)
		if (!res.ok) return null
		const body = await res.json().catch(() => ({}))
		return typeof body?.form_code === 'string' ? body.form_code : null
	} catch {
		return null
	}
}

/**
 * Sponsor forgot-password — request step. Always resolves (Studio itself
 * never reveals whether the email has an account); the caller shows a
 * generic "check your email" message either way.
 */
export async function requestSponsorPasswordReset(
	email: string,
	locale = ''
): Promise<{ detail: string }> {
	const res = await fetch(
		`${studioBase()}/api/client/v1/sponsor/password-reset/request/`,
		{
			method: 'POST',
			headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
			body: JSON.stringify({ email, ...(locale ? { locale } : {}) }),
		}
	)
	if (!res.ok) {
		const detail = await res.text()
		throw new Error(
			`Studio sponsor password-reset request failed (${res.status}): ${detail.slice(0, 300)}`
		)
	}
	return (await res.json()) as { detail: string }
}

/**
 * Sponsor forgot-password — confirm step. Throws Error with Studio's own
 * detail message (invalid/expired token, password too short) so the
 * caller can show it directly rather than a generic failure.
 */
export async function confirmSponsorPasswordReset(
	token: string,
	password: string
): Promise<{ ok: boolean; email: string }> {
	const res = await fetch(
		`${studioBase()}/api/client/v1/sponsor/password-reset/confirm/`,
		{
			method: 'POST',
			headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
			body: JSON.stringify({ token, password }),
		}
	)
	const body = await res.json().catch(() => ({}))
	if (!res.ok) {
		throw new Error(
			(body as { detail?: string }).detail ??
				`Studio sponsor password-reset confirm failed (${res.status})`
		)
	}
	return body as { ok: boolean; email: string }
}

/**
 * Submit a sponsor member's self-completion form, attaching the invite token
 * so Studio can stamp the roster row (§5 of the plan). Kept as its own
 * function rather than extending submitToStudio's signature — that function
 * is shared by every other form submission in the app and shouldn't grow an
 * invite-token-shaped parameter no other caller needs.
 */
export interface SponsorInviteContext {
	form_code: string
	member_name: string
	status: string
}

export class SponsorInviteError extends Error {
	status: number
	reason: string
	constructor(status: number, reason: string) {
		super(reason)
		this.status = status
		this.reason = reason
	}
}

/**
 * Resolve an invite token to the completion form + member name. Public on
 * Studio's side (the token IS the auth) — no STUDIO_API_KEY needed. Used
 * server-side in getServerSideProps for /sponsor/complete/[token].
 */
export async function resolveSponsorInvite(
	token: string
): Promise<SponsorInviteContext> {
	const res = await fetch(
		`${studioBase()}/api/client/v1/sponsor/invites/${encodeURIComponent(token)}/`,
		{ signal: AbortSignal.timeout(10000) }
	)
	const body = await res.json().catch(() => ({}))
	if (!res.ok) {
		throw new SponsorInviteError(res.status, body?.detail ?? 'invalid_token')
	}
	return body as SponsorInviteContext
}

export interface PolicyVerificationResult {
	policy_number: string
	status: string
	insured_name: string
	product_name: string
	policy_start_date: string | null
	policy_end_date: string | null
	insurer_name: string
}

export class PolicyVerificationError extends Error {
	status: number
	constructor(status: number) {
		super('policy_not_found')
		this.status = status
	}
}

/**
 * Look up a policy for the public QR-verification page. Public on Studio's
 * side (the UUID in the URL IS the auth — it's the same PolicyIssuance
 * primary key printed as a QR on the certificate) — no STUDIO_API_KEY
 * needed, same reasoning as resolveSponsorInvite above.
 */
export async function fetchPolicyVerification(
	policyId: string
): Promise<PolicyVerificationResult> {
	const res = await fetch(
		`${studioBase()}/api/client/v1/policy-verification/${encodeURIComponent(policyId)}/`,
		{ signal: AbortSignal.timeout(10000) }
	)
	if (!res.ok) throw new PolicyVerificationError(res.status)
	return (await res.json()) as PolicyVerificationResult
}

export async function submitSponsorCompletionToStudio(
	formCode: string,
	data: Record<string, unknown>,
	inviteToken: string
): Promise<StudioSubmitResponse> {
	const res = await fetch(
		`${studioBase()}/api/client/v1/forms/${formCode}/submit/`,
		{
			method: 'POST',
			headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
			body: JSON.stringify({
				data,
				source_channel: 'sponsor_invite',
				invite_token: inviteToken,
			}),
			signal: AbortSignal.timeout(10000),
		}
	)
	if (!res.ok) {
		const body = await res.json().catch(() => ({}))
		throw new StudioApiError(res.status, body)
	}
	return res.json() as Promise<StudioSubmitResponse>
}

// ---------------------------------------------------------------------------
// Inson KASKO — vehicle catalogue, all-plan pricing, file storage
//
// Three endpoints the shared @crosure/tenant-runtime client doesn't wrap yet,
// so they call the Client API directly like fetchPageSchema above.
//
// NOTE: the form schema advertises different paths for these
// (`payment/product/car-insurance/all-premium`, `common/files/`) — those are
// inherited from the legacy Indo backend and do not exist on Studio. The
// canonical routes are the ones used here; see client_urls.py.
// ---------------------------------------------------------------------------

/** `factors.car_price[make][model][modelYear] = sumInsured`. */
export interface StudioFormFactors {
	factors: {
		car_price?: Record<string, Record<string, Record<string, number>>>
		[key: string]: unknown
	}
}

export interface StudioAllPremiums {
	currency: string
	/** Programme code → premium, as a decimal string ("3200000.00"). */
	premiums: Record<string, string>
}

export interface StudioUploadedFile {
	upload_id: string
	name: string
	size_bytes: number
	mime_type: string
	url: string
}

/**
 * Dynamic factor data backing the schema's `x-crosure-options-source` fields.
 * One call returns the whole make → model → year price map (~20KB), which the
 * cascade then resolves client-side.
 */
export async function fetchStudioFormFactors(
	formCode: string
): Promise<StudioFormFactors> {
	const url = `${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/factors/`
	const res = await fetch(url, {
		headers: studioAuthHeaders(),
		signal: AbortSignal.timeout(12000),
	})
	if (!res.ok) {
		const body = await res.json().catch(() => ({}))
		throw new StudioApiError(res.status, body)
	}
	return res.json() as Promise<StudioFormFactors>
}

/**
 * Price every programme from one vehicle selection, without persisting a quote
 * — so the plans step can show all three cards at once. Studio rejects an
 * unknown make/model/modelYear combination with a 400.
 */
export async function fetchStudioAllPremiums(
	formCode: string,
	data: Record<string, unknown>
): Promise<StudioAllPremiums> {
	const url = `${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/all-premiums/`
	const res = await fetch(url, {
		method: 'POST',
		headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
		body: JSON.stringify({ data }),
		signal: AbortSignal.timeout(20000),
	})
	if (!res.ok) {
		const body = await res.json().catch(() => ({}))
		throw new StudioApiError(res.status, body)
	}
	return res.json() as Promise<StudioAllPremiums>
}

export type StudioPlanPrices = {
	currency: string
	field: string
	prices: Record<string, number | null>
}

/** "From" price per plan, priced by the AI brain — see Studio services/plan_prices.py. */
export async function fetchStudioPlanPrices(
	formCode: string,
	data: Record<string, unknown>
): Promise<StudioPlanPrices> {
	const url = `${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/plan-prices/`
	const res = await fetch(url, {
		method: 'POST',
		headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
		body: JSON.stringify({ data }),
		signal: AbortSignal.timeout(20000),
	})
	if (!res.ok) {
		const body = await res.json().catch(() => ({}))
		throw new StudioApiError(res.status, body)
	}
	return res.json() as Promise<StudioPlanPrices>
}

/**
 * Store a file against the tenant and return its metadata. `url` is what lands
 * in the form value for an upload field (`x-crosure-upload.storesAs: "url"`).
 * Requires the API key to hold the `files:write` scope.
 *
 * Takes the multipart body verbatim rather than a parsed file: the proxy route
 * forwards exactly what the browser sent, so the boundary in `contentType`
 * still matches the payload. Re-parsing and re-encoding would only risk
 * corrupting binary image data for no gain.
 */
export async function uploadStudioFile(
	body: Buffer,
	contentType: string
): Promise<StudioUploadedFile> {
	const res = await fetch(`${studioBase()}/api/client/v1/uploads/`, {
		method: 'POST',
		headers: { ...studioAuthHeaders(), 'Content-Type': contentType },
		body: new Uint8Array(body),
		signal: AbortSignal.timeout(60000),
	})
	if (!res.ok) {
		const payload = await res.json().catch(() => ({}))
		throw new StudioApiError(res.status, payload)
	}
	return res.json() as Promise<StudioUploadedFile>
}

export interface StudioVerifiedPayment {
	/** 'succeeded' on the happy path; 'pending' comes back as a 409, not here. */
	status: string
	detail?: string
	[key: string]: unknown
}

/**
 * Confirm a completed checkout from the return leg.
 *
 * The PSP webhook remains the authoritative signal — this is Studio's own
 * idempotent fallback for when that webhook is late or lost, so the customer
 * isn't shown "payment pending" on a charge that actually cleared. Safe to call
 * more than once for the same intent.
 *
 * Studio answers a still-processing intent with 409, which is a real answer
 * rather than a failure: callers should surface "still processing", not an error.
 *
 * `reference` is Studio's own reference for the checkout. Gateways that mint
 * the payment id outside Studio (Moyasar in the browser, Klarna's order id)
 * return an id Studio never stored, so without it Studio cannot find the
 * transaction — nor, therefore, the gateway to verify against.
 */
export async function verifyStudioPayment(
	formCode: string,
	intentId: string,
	locale?: string,
	reference?: string
): Promise<StudioVerifiedPayment> {
	const url = `${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/verify-payment/`
	const res = await fetch(url, {
		method: 'POST',
		headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
		body: JSON.stringify({
			intent_id: intentId,
			locale,
			...(reference ? { reference } : {}),
		}),
		signal: AbortSignal.timeout(20000),
	})
	if (!res.ok) {
		const body = await res.json().catch(() => ({}))
		throw new StudioApiError(res.status, body)
	}
	return res.json() as Promise<StudioVerifiedPayment>
}

export interface SendStudioFormActionPayload {
	action_type?: string
	destination?: string
	phone_number?: string
	email?: string
	country_code?: string
	element_id?: string
	section_code?: string
	locale?: string
	subject?: string
	message?: string
}

export type SendStudioFormLinkPayload = SendStudioFormActionPayload

export interface SendStudioFormActionResponse {
	sent?: boolean
	ok?: boolean
	action_type?: string
	destination?: string
	phone_number?: string
	email?: string
	link?: string
	detail?: string
}

export type SendStudioFormLinkResponse = SendStudioFormActionResponse

/**
 * Dispatch an action (SMS, Email, etc.) from an Information section button.
 */
export async function sendStudioFormAction(
	formCode: string,
	payload: SendStudioFormActionPayload
): Promise<SendStudioFormActionResponse> {
	const url = `${studioBase()}/api/client/v1/forms/${encodeURIComponent(formCode)}/send-link/`
	const res = await fetch(url, {
		method: 'POST',
		headers: { ...studioAuthHeaders(), 'Content-Type': 'application/json' },
		body: JSON.stringify(payload),
		signal: AbortSignal.timeout(15000),
	})
	if (!res.ok) {
		const body = await res.json().catch(() => ({}))
		throw new StudioApiError(res.status, body)
	}
	return res.json() as Promise<SendStudioFormActionResponse>
}

export const sendStudioFormLink = sendStudioFormAction
